import {
  isRoundV3,
  type RoundSnapshot,
  type RoundSnapshotV3,
} from "../../../../domain/round/round-types.js";
import type { RoundJournal } from "../../../data/round-journal.js";
import type { RoundSyncEngine } from "../../../sync/round-sync-engine.js";
import type { ReviewedImportInput } from "./ggb-round.js";
import { importValue } from "./ggb-import-value.js";

interface Options {
  accountId: string;
  engine: RoundSyncEngine;
  journal: RoundJournal;
  input: ReviewedImportInput;
  /** Must throw when the account, connection or season setup has changed. */
  access(): void;
  readConfirmedRound(roundId: string): Promise<RoundSnapshot>;
}

const inFlight = new WeakMap<
  RoundSyncEngine,
  { input: string; job: Promise<RoundSnapshot> }
>();

/** Coalesce double taps while keeping a different request out of this engine. */
export function executeReviewedImport(
  options: Options,
): Promise<RoundSnapshot> {
  const key = importValue(options.input);
  const pending = inFlight.get(options.engine);
  if (pending)
    return pending.input === key
      ? pending.job
      : Promise.reject(new Error("Another import is already running."));
  const job = execute({ ...options, input: structuredClone(options.input) });
  inFlight.set(options.engine, { input: key, job });
  void job.then(
    () => inFlight.delete(options.engine),
    () => inFlight.delete(options.engine),
  );
  return job;
}

/** Replays the existing journal. It never replaces a partially saved import. */
async function execute(options: Options): Promise<RoundSnapshot> {
  const { accountId, engine, journal, input } = options;
  options.access();
  if (
    input.setup.schemaVersion !== 3 ||
    input.setup.rules.kind !== "ggb-import"
  )
    throw new Error("This action only imports reviewed GameBook rounds.");
  const recoverable = await journal.getLatestRecoverableSnapshot(accountId);
  if (recoverable && recoverable.roundId !== input.roundId)
    throw new Error("Finish or abandon your open round before importing.");
  options.access();
  let snapshot = await journal.getSnapshot(accountId, input.roundId);
  if (!snapshot) {
    snapshot = await engine.createRound({
      roundId: input.roundId,
      ...input.setup,
    });
  }
  requireSameImport(snapshot, input, accountId);
  snapshot = await flushConfirmed(options);
  if (snapshot.state === "draft") {
    options.access();
    await engine.startRound(input.roundId);
    snapshot = await flushConfirmed(options);
  }
  if (snapshot.state === "active") {
    await enterMissingScores(options, snapshot);
    snapshot = await flushConfirmed(options);
    requireCompleteScores(snapshot, input);
    options.access();
    await engine.finishRound(input.roundId);
    snapshot = await flushConfirmed(options);
  }
  if (snapshot.state !== "completed")
    throw new Error(
      "The saved import cannot be completed in its current state.",
    );
  const confirmed = await options.readConfirmedRound(input.roundId);
  options.access();
  requireSameImport(confirmed, input, accountId);
  requireCompleteScores(confirmed, input);
  if (confirmed.state !== "completed" || confirmed.version < snapshot.version)
    throw new Error(
      "The server has not confirmed the completed import. Continue this import to retry.",
    );
  await journal.acceptLiveSnapshot(accountId, confirmed);
  options.access();
  if (await journal.hasUnsynced(accountId))
    throw new Error(
      "New changes still need confirmation. Continue the saved import.",
    );
  return confirmed;
}

async function flushConfirmed(options: Options): Promise<RoundSnapshot> {
  options.access();
  const snapshot = await options.engine.flush(options.input.roundId);
  options.access();
  if (
    options.engine.getConflicts().length ||
    (await options.journal.hasUnsynced(options.accountId))
  )
    throw new Error(
      "The import still has unconfirmed changes. Continue it to sync the same saved commands.",
    );
  requireSameImport(snapshot, options.input, options.accountId);
  return snapshot;
}

async function enterMissingScores(
  options: Options,
  initial: RoundSnapshot,
): Promise<void> {
  let snapshot = initial;
  for (const hole of options.input.setup.orderedHoleNumbers ?? []) {
    for (const participant of options.input.participants) {
      options.access();
      const old = snapshot.holeEntries?.[participant.id]?.[hole];
      const strokes = participant.scores[hole];
      if (strokes === undefined)
        throw new Error("A reviewed score is missing.");
      if (old && old.status !== "unset") {
        requireImportedEntry(old, strokes);
        continue;
      }
      snapshot = await options.engine.setHoleEntry(
        options.input.roundId,
        participant.id,
        hole,
        {
          status: "scored",
          strokes,
          putts: null,
          fairwayHit: null,
          greenInRegulation: null,
          source: "import",
        },
      );
    }
  }
}

function requireImportedEntry(
  entry: RoundSnapshotV3["holeEntries"][string][number],
  strokes: number,
): void {
  if (
    entry.status !== "scored" ||
    entry.strokes !== strokes ||
    entry.source !== "import" ||
    entry.putts !== null ||
    entry.fairwayHit !== null ||
    entry.greenInRegulation !== null
  )
    throw new Error(
      "This import's saved score has changed. Review the existing round; it will not be overwritten.",
    );
}

function requireCompleteScores(
  snapshot: RoundSnapshot,
  input: ReviewedImportInput,
): void {
  for (const participant of input.participants) {
    for (const hole of input.setup.orderedHoleNumbers ?? []) {
      const entry = snapshot.holeEntries?.[participant.id]?.[hole];
      if (!entry) throw new Error("The server is missing an imported hole.");
      requireImportedEntry(entry, participant.scores[hole]!);
    }
  }
}

function requireSameImport(
  snapshot: RoundSnapshot,
  input: ReviewedImportInput,
  accountId: string,
): void {
  const { setup } = input;
  const equal = (left: unknown, right: unknown) =>
    importValue(left) === importValue(right);
  if (
    !isRoundV3(snapshot) ||
    snapshot.roundId !== input.roundId ||
    snapshot.scorekeeperId !== accountId ||
    !equal(snapshot.course, setup.course) ||
    !equal(snapshot.conditions, setup.conditions) ||
    !equal(snapshot.rules, setup.rules) ||
    !equal(snapshot.orderedHoleNumbers, setup.orderedHoleNumbers) ||
    snapshot.participants.length !== setup.participants.length ||
    !setup.participants.every((player, index) => {
      const saved = snapshot.participants[index];
      return (
        saved?.id === player.id &&
        equal(saved.teeSnapshot, player.teeSnapshot) &&
        equal(
          saved.handicapSnapshot && {
            ...saved.handicapSnapshot,
            agreedAt: null,
          },
          player.handicapSnapshot && {
            ...player.handicapSnapshot,
            agreedAt: null,
          },
        )
      );
    }) ||
    !setup.mainContest ||
    snapshot.mainContest.id !== setup.mainContest.id ||
    snapshot.mainContest.format !== setup.mainContest.format ||
    snapshot.mainContest.scoringBasis !== setup.mainContest.scoringBasis ||
    !equal(snapshot.mainContest.sides, setup.mainContest.sides)
  )
    throw new Error(
      "The saved round does not match this locked import. Its existing facts were preserved.",
    );
}
